import React, { useEffect } from 'react';
import { FaGraduationCap, FaBriefcase, FaHeartbeat, FaHome, FaChalkboardTeacher, FaHandsHelping, FaMicrophone, FaWheelchair } from 'react-icons/fa';

const Services = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const services = [
    {
      icon: <FaGraduationCap className="text-5xl text-primary" />,
      title: ' Prevention Programs',
      description: 'Early identification is the key to preventing disability: ',
      features: ['Eye-screening camps across villages ', 'Awareness programs on eye health and preventable disabilities ', 'Early detection drives for children and adults ', 'Community outreach through local health workers '],
      image: 'https://images.unsplash.com/photo-1709290749293-c6152a187b14',
    },
    {
      icon: <FaBriefcase className="text-5xl text-secondary" />,
      title: 'Cure & Medical Services',
      description: 'For individuals identified with eye-related issues, the Society has offered:',
      features: ['Free and subsidized eye surgeries ', 'Medical treatments ', 'Follow-up care and counseling'],
      image: 'https://plus.unsplash.com/premium_photo-1663075871632-88bc4a73b3b4?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mjl8fEN1cmUlMjAlMjYlMjBNZWRpY2FsJTIwU2VydmljZXN8ZW58MHx8MHx8fDA%3D',
    },
    {
      icon: <FaHeartbeat className="text-5xl text-success" />,
      title: 'Rehabilitation Services',
      description: 'Helping individuals regain independence is at the heart of our mission: ',
      features: ['Training in daily living skills for the blind ', 'Mobility and orientation training ', 'Social and emotional support ', 'Vocational skill development ','Community reintegration support '],
      image: 'https://media.istockphoto.com/id/1295969880/photo/a-confident-doctor-woman-in-a-safety-medical-mask-is-examining-a-mature-patient-in-the-mask.jpg?s=612x612&w=0&k=20&c=eX71MHFMxQkhClYI_MF_JOeVCxbHJkBiEQzqNTKd4TI=',
    },
    {
      icon: <FaHome className="text-5xl text-accent" />,
      title: 'Livelihood Support Programs',
      description: 'To ensure long-term sustainability, we provide economic support such as:',
      features: ['Funding for small shops and micro-businesses ', 'Support for STD booths ', 'Training in chair caning ', 'Support for animal rearing (goats, cattle, etc.) '],
      image: 'https://i.pinimg.com/1200x/b4/71/2c/b4712cebbbbdce3651bfd9525f2d5043.jpg',
    },
    {
      icon: <FaChalkboardTeacher className="text-5xl text-primary" />,
      title: 'Multi-Disability Vocational Training',
      description: 'We empower persons with multiple disabilities through skill-building in:',
      features: ['Envelope making ', 'Notebook manufacturing ', 'Packaging and small-scale production '],
      image: 'https://i.pinimg.com/736x/07/09/d8/0709d824311d23237a681e5ea6081608.jpg',
    },
    {
      icon: <FaHandsHelping className="text-5xl text-secondary" />,
      title: 'Audiobook Production for the Visually Impaired',
      description: 'Launched in 2007, our audiobook initiative has: ',
      features: ['Enabled blind students to pursue higher education ', 'Made competitive exam preparation accessible ', 'Helped many graduates become doctors, professors, and scholars '],
      image: 'https://media.istockphoto.com/id/1267387917/photo/disability-young-blind-person-happy-woman-in-headphone-typing-on-computer-keyboard-working-in.jpg?s=612x612&w=0&k=20&c=GEk3fHOy6A3wnIs0qE1HqWycGFRYnjO_hJ2J5Kxxvw0=',
    },
    {
      icon: <FaMicrophone className="text-5xl text-success" />,
      title: 'Online Skill Development',
      description: 'To keep up with the digital age, we conduct: ',
      features: ['Three online training sessions every month', 'Courses covering computer basics, communication, soft skills, and employability skills '],
      image: 'https://media.istockphoto.com/id/1444484875/photo/asian-young-dj-man-speaking-on-microphone-at-studio-with-loving-parent-male-teenager-special.jpg?s=612x612&w=0&k=20&c=brzCY5EPKe6SvHgRZErYKaN6hWU4gWkwsmMJ34UTmKs=',
    },
    {
      icon: <FaWheelchair className="text-5xl text-accent" />,
      title: ' Job Placement & Career Support',
      description:'We regularly conduct: ',
      features: ['Placement drives ', ' Job mapping and counselling  ','Resume preparation and interview skills sessions '],
      image: 'https://media.istockphoto.com/id/1482147447/photo/business-team-meeting-in-modern-office-sitting-area.jpg?s=612x612&w=0&k=20&c=OxH0SYI2WIAOKcBxF8O-xlE0eGshi7X-2N2mCMkdM5w=',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-secondary to-success text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="services-hero-title">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-95">
              Comprehensive programs designed to empower, support, and transform lives
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a wide range of services tailored to meet the diverse needs of individuals with disabilities 
              and their families. Each program is designed with care, compassion, and a commitment to excellence.
            </p>
          </div>

          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-xl shadow-xl w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="flex items-center mb-4">
                    {service.icon}
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-800 ml-4">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center text-gray-700">
                        <svg
                          className="w-5 h-5 text-success mr-3 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Grid Section */}
      <section className="py-16 bg-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4" data-testid="gallery-title">
              Our Programs in Action
            </h2>
            <p className="text-gray-600 text-lg">
              Glimpses of our vibrant community and the positive impact we create every day
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              'https://images.unsplash.com/photo-1756454487569-903cdde4b92a',
              'https://images.unsplash.com/photo-1709290749293-c6152a187b14',
              'https://images.unsplash.com/photo-1692269725836-fbd72e98883f',
              'https://images.unsplash.com/photo-1692269725911-87697c558be1',
              'https://media.istockphoto.com/id/1444484875/photo/asian-young-dj-man-speaking-on-microphone-at-studio-with-loving-parent-male-teenager-special.jpg?s=612x612&w=0&k=20&c=brzCY5EPKe6SvHgRZErYKaN6hWU4gWkwsmMJ34UTmKs=',
              'https://images.unsplash.com/photo-1699547283492-7a8b85165edd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxpbmRpYSUyMGRpc2FiaWxpdHl8ZW58MHx8fHwxNzY0NTY4MjMyfDA&ixlib=rb-4.1.0&q=85',
              'https://images.unsplash.com/photo-1764006145420-df3006edf060?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHx3aGVlbGNoYWlyJTIwc3VwcG9ydHxlbnwwfHx8fDE3NjQ1NjgyMzh8MA&ixlib=rb-4.1.0&q=85',
              'https://images.pexels.com/photos/28357557/pexels-photo-28357557.jpeg',
            ].map((img, index) => (
              <div
                key={index}
                data-testid={`gallery-image-${index}`}
                className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-all duration-300 group aspect-square"
              >
                <img
                  src={img}
                  alt={`Program ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">
            Want to Learn More About Our Services?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Get in touch with us to discuss how we can support you or your loved ones
          </p>
          <a
            href="mailto:info@helenkellersociety.org"
            data-testid="contact-email-btn"
            className="inline-block bg-gradient-to-r from-primary to-secondary text-white px-10 py-4 rounded-full font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;